import {StickMaterial} from "./StickMaterial";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";
import {joinedRootId, stepVoids, sticksById} from "../../model/frame/MdFrameBlock";
import {geo} from "./Joints";
import {inches} from "../../const";

// alignment slop when matching stick positions.
const WIGGLE = inches / 16;

// draw each joined step (see BlockOps.joinSticks) as part of the step it joined: the
// joined stick's piece is hidden and the other runs across both, and a stick in the
// gap between them stops at the joined stick's face. Their steps share one size, so
// the two sticks lie on one line.
export function mergeJoinedSteps(spec: MdFrameSpec, sticks: StickMaterial[]) {
    const model = sticksById(spec.frameBlocks);
    const visible = () => sticks.filter(s => !s.destroyed && s.graphic.visible && s.length.get() > 0);

    for(const stickId of stepVoids(spec.frameBlocks).keys()) {
        const rootId = joinedRootId(model, stickId);
        if(rootId === stickId) continue;

        const shown = visible();
        const a = shown.find(s => s.id === rootId);
        const b = shown.find(s => s.id === stickId);
        if(!a || !b || a.orientation !== b.orientation) continue;

        const ga = geo(a);
        const gb = geo(b);
        if(Math.abs(ga.crossMin - gb.crossMin) > WIGGLE) continue;

        const min = Math.min(ga.alongMin, gb.alongMin);
        const max = Math.max(ga.alongMax, gb.alongMax);
        const gapMin = Math.min(ga.alongMax, gb.alongMax);
        const gapMax = Math.max(ga.alongMin, gb.alongMin);

        b.graphic.visible = false;
        if(a.orientation === "horizontal") a.graphic.position.set(min, a.positionY);
        else a.graphic.position.set(a.positionX, min);
        a.length.set(max - min, {debug: "joined-step"});
        a.modelEnds = {
            A: ga.alongMin <= gb.alongMin ? a.modelEnds.A : b.modelEnds.A,
            B: ga.alongMax >= gb.alongMax ? a.modelEnds.B : b.modelEnds.B,
        };

        // a stick across the gap stops at the joined stick's near face instead of
        // running past it.
        for(const o of shown) {
            if(o === a || o === b || o.orientation === a.orientation) continue;

            const g = geo(o);
            if(g.crossMin < gapMin - WIGGLE || g.crossMax > gapMax + WIGGLE) continue;

            if(g.alongMin < ga.crossMin && g.alongMax > ga.crossMin) {
                o.length.set(ga.crossMin - g.alongMin, {debug: "joined-step"});
            } else if(g.alongMin >= ga.crossMin - WIGGLE && g.alongMin < ga.crossMax) {
                const length = g.alongMax - ga.crossMax;
                if(length <= 0) {
                    o.graphic.visible = false;
                    continue;
                }
                if(o.orientation === "vertical") o.graphic.position.set(o.positionX, ga.crossMax);
                else o.graphic.position.set(ga.crossMax, o.positionY);
                o.length.set(length, {debug: "joined-step"});
            }
        }
    }
}

// two sticks on one line that meet end to end across a crossing stick which stops at
// their far face (a void lies beyond, so its piece ends there): the crossing stick
// caps on their near face, and the two meet at the centre of its thickness. Two
// split-off parts of one outline edge stay two sticks, and a mullion between their
// cells tees into the outline instead of running under it.
export function meetAcrossSticks(sticks: StickMaterial[]) {
    const shown = sticks.filter(s => !s.destroyed && s.graphic.visible && s.length.get() > 0);

    for(const a of shown) {
        for(const b of shown) {
            if(b === a || b.orientation !== a.orientation) continue;

            const ga = geo(a);
            const gb = geo(b);
            if(Math.abs(ga.crossMin - gb.crossMin) > WIGGLE || gb.alongMin < ga.alongMax - WIGGLE) continue;

            const gapMin = ga.alongMax;
            const gapMax = gb.alongMin;
            if(gapMax - gapMin <= WIGGLE) continue;

            const across = shown.find(o => {
                if(o === a || o === b || o.orientation === a.orientation) return false;
                const g = geo(o);
                return Math.abs(g.crossMin - gapMin) < WIGGLE && Math.abs(g.crossMax - gapMax) < WIGGLE;
            });
            if(!across) continue;

            const g = geo(across);
            if(Math.abs(g.alongMax - ga.crossMax) < WIGGLE && g.alongMin < ga.crossMin - WIGGLE) {
                across.length.set(ga.crossMin - g.alongMin, {debug: "meet-across"});
            } else if(Math.abs(g.alongMin - ga.crossMin) < WIGGLE && g.alongMax > ga.crossMax + WIGGLE) {
                setAlong(across, ga.crossMax);
                across.length.set(g.alongMax - ga.crossMax, {debug: "meet-across"});
            } else {
                continue;
            }

            const mid = (gapMin + gapMax) / 2;
            a.length.set(mid - ga.alongMin, {debug: "meet-across"});
            setAlong(b, mid);
            b.length.set(gb.alongMax - mid, {debug: "meet-across"});
        }
    }
}

// move a stick's start along its length.
function setAlong(s: StickMaterial, at: number) {
    if(s.orientation === "horizontal") s.graphic.position.set(at, s.positionY);
    else s.graphic.position.set(s.positionX, at);
}
